/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
// Ember Imports
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External imports
import moment from 'moment';
// Internal imports
import ExpulsionOutcome from 'linced-app/enums/expulsion-outcome';
import ExpulsionStage from 'linced-app/enums/expulsion-stage';
import { SuspensionCategory } from '@linc-technologies/linced-common/enums/suspensions';
import { defaultTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import { action, computed } from '@ember/object';

export default class ExpulsionNextStage extends Component {
  // Services
  @service
  behaviourDates;

  @service
  countdownButton;

  @service
  dates;

  @service
  expulsions;

  @service
  session;

  @service
  store;

  @service
  toaster;

  @service
  uppy;

  // Enums
  ExpulsionOutcome = ExpulsionOutcome;

  ExpulsionStage = ExpulsionStage;

  // Constants
  editorOptions = Object.freeze(defaultTinyMCEConfig());

  // Properties
  close = null;

  expulsion = null;
  expulsionStartDate = '';
  @tracked
  previewingLetters = false;
  @tracked
  previousSuspensions = [];
  principal = null;
  @tracked
  saveInProgress = false;
  stage = ExpulsionStage.PENDING_EXPULSION;
  suspensionLetterPage = null;
  suspensionPostPage = null;

  factualDetailsOfConcernConfig = Object.freeze(
    Object.assign(
      {
        placeholder:
          'Add details about the behaviours that led to this expulsion here',
      },
      defaultTinyMCEConfig(),
    ),
  );

  specificDetailsOfDocumentsConfig = Object.freeze(
    Object.assign(
      {
        placeholder:
          'Enter specific details of documents here, for example, ' +
          'previous formal cautions to suspend, previous notifications of ' +
          'suspension, individual student support planning documents, return ' +
          'to school planning documents, relevant meeting minutes, relevant ' +
          'learning and support documents, risk management plans',
      },
      defaultTinyMCEConfig(),
    ),
  );

  reasonForDecisionConfig = Object.freeze(
    Object.assign(
      {
        placeholder:
          'Please specify the reasons for this outcome for this expulsion.',
      },
      defaultTinyMCEConfig(),
    ),
  );

  letterConfig = Object.freeze(
    Object.assign(
      {
        height: 500,
      },
      defaultTinyMCEConfig(),
    ),
  );

  @tracked
  lettersPreview;

  @tracked
  reviewing;

  @computed('previousSuspensions.[]')
  get filteredPreviousSuspensions() {
    return this.previousSuspensions.filter((suspension) => {
      return suspension.category === SuspensionCategory.SUSPENSION;
    });
  }

  @computed('filteredPreviousSuspensions.@each.{startDate,displayEndDate}')
  get currentlySuspendedDays() {
    if (this.filteredPreviousSuspensions.length === 0) {
      return [];
    }
    let suspendedDays = [];
    this.filteredPreviousSuspensions.forEach((suspension) => {
      let currentDay = moment(suspension.startDate, 'YYYY-MM-DD');
      while (currentDay.isSameOrBefore(suspension.displayEndDate, 'day')) {
        let formattedDay = currentDay.format('YYYY-MM-DD');
        if (this.behaviourDates.validateIsSchoolDay(formattedDay)) {
          suspendedDays.push(formattedDay);
        }
        currentDay.add(1, 'days');
      }
    });
    return suspendedDays;
  }

  init() {
    super.init(...arguments);

    let nextSchoolDay = this.behaviourDates.getNextSchoolDay(moment(), 1);

    if (isEmpty(this.get('expulsion.caregivers'))) {
      // Plain array, not A(): caregivers is a @hasMany relationship, and
      // Ember Data's hasMany setter never stores the passed-in array
      // itself - it spreads its items into a splice() call on the real
      // ManyArray, so the wrapper type of the input is irrelevant
      // (verified against the installed ember-data/model source).
      this.expulsion.set('caregivers', []);
    }

    // Send expulsions service context
    this.expulsions.initialise(
      this.expulsion,
      this.principal,
      this.suspensionLetterPage,
      this.suspensionPostPage,
    );

    this.expulsion.set('stage', this.stage);
    let maxSuspensionDate;
    let defaultReviewDate;

    // Setup defaults for expulsion creation/editing
    switch (this.stage) {
      case ExpulsionStage.WRITTEN_NOTICE_OF_POTENTIAL_EXPULSION:
        this.store
          .query('attendance/suspension', {
            schoolId: this.get('session.school.id'),
            learnerId: this.get('expulsion.learner.id'),
            dateFrom: moment().startOf('year').format('YYYY-MM-DD'),
            dateTo: moment().endOf('year').format('YYYY-MM-DD'),
          })
          .then((res) => {
            this.previousSuspensions = res;
          });
        maxSuspensionDate = this.behaviourDates.getLastValidSchoolDay(
          nextSchoolDay,
          this.behaviourDates.maxDaysSuspended,
        );
        defaultReviewDate = this.behaviourDates.getLastValidSchoolDay(
          nextSchoolDay,
          this.behaviourDates.reviewDateDefaultDays,
        );
        this.expulsion.setProperties({
          suspensionStartDate: nextSchoolDay,
          reviewDate: defaultReviewDate,
          suspensionEndDate: maxSuspensionDate,
          title: this.expulsion.get('incident.displayCategory'),
          noticeDate: moment().format('YYYY-MM-DD'),
        });
        this.reviewing = true;
        this.countdownButton.initialise(3);
        break;

      case ExpulsionStage.EXPELLED:
        this.expulsion.setProperties({
          expulsionStartDate: nextSchoolDay,
          outcome: ExpulsionOutcome.PLACEMENT_IN_ANOTHER_SCHOOL,
        });
        break;

      case ExpulsionStage.NOT_EXPELLED:
        this.expulsion.setProperties({
          earlyReturnDate: nextSchoolDay,
        });
        break;

      default:
        break;
    }

    this.lettersPreview = this.expulsions.getLettersPreview();
  }

  willDestroyElement() {
    super.willDestroyElement(...arguments);
    this.countdownButton.cleanup();
  }

  @action
  async nextStage() {
    this.saveInProgress = true;

    this.expulsion.set('stage', this.stage);

    try {
      await this.expulsions.expulsionSavePipeline(this.lettersPreview);
    } catch (err) {
      this.toaster.error('Failed to save expulsion/letters: ' + err);
    }

    this.close();
    this.highlightExpulsion(this.expulsion);
  }

  @action
  previewLetters() {
    this.previewingLetters = true;
  }

  @action
  removeSelectedPerson(prop) {
    this.expulsion.set(prop, null);
  }

  @action
  async addCaregiver(person) {
    // caregivers is an async hasMany: edit the loaded ManyArray, not the
    // PromiseManyArray, which has no push/splice.
    (await this.expulsion.caregivers).push(person);
    this.lettersPreview = this.expulsions.getLettersPreview();
  }

  @action
  async removeCaregiver(person) {
    let caregivers = await this.expulsion.caregivers;
    let index = caregivers.indexOf(person);
    if (index !== -1) {
      caregivers.splice(index, 1);
    }
    this.lettersPreview = this.expulsions.getLettersPreview();
  }

  @action
  addAttachments() {
    this.uppy.addFiles();
  }

  @action
  removeAttachment(attachment) {
    this.expulsions.removeAttachment(attachment);
  }

  @action
  back() {
    this.previewingLetters = false;
  }

  @action
  setOutcome(outcome) {
    this.expulsion.set('outcome', outcome.value);
  }
}
